"use client";

import { useEffect } from "react";
import { appOrigin } from "@/config/env";
import { blogSeoFields } from "@/platforms/customer/utils/blogSeo";

function upsertMeta(attr, key, content) {
  if (!content) return;
  const sel = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Sets document title, meta description, OG/Twitter tags, canonical, and Article JSON-LD
 * for client navigations. Prefer appOrigin so canonical matches SSR metadataBase.
 * Skips re-injecting JSON-LD when the server already rendered #blog-article-jsonld.
 */
export default function BlogSeoHead({ blog, pathKey = "" }) {
  useEffect(() => {
    if (!blog) return;
    const seo = blogSeoFields(blog, {
      origin: appOrigin(),
      pathKey,
    });

    document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "title", seo.title);
    upsertMeta("name", "robots", "index, follow");
    upsertLink("canonical", seo.canonical);

    upsertMeta("property", "og:type", "article");
    upsertMeta("property", "og:site_name", "TripsAdora");
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", seo.canonical);
    upsertMeta("property", "og:image", seo.image);
    if (seo.publishedAt) {
      upsertMeta("property", "article:published_time", String(seo.publishedAt));
    }
    if (seo.updatedAt) {
      upsertMeta("property", "article:modified_time", String(seo.updatedAt));
    }

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", seo.image);

    if (!document.getElementById("blog-article-jsonld")) {
      upsertJsonLd("blog-article-jsonld", seo.schemas);
    }
  }, [blog, pathKey]);

  return null;
}
